import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    AlertTriangle, Save, RefreshCw, Loader2, Wrench, Power,
    Smartphone, Apple, Globe, Copy, Check, Link2, KeyRound, Clock,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { cn } from '@/lib/utils';

// ─── Types ──────────────────────────────────────────────────────────────────

type Platform = 'ios' | 'android' | 'web';

interface MaintenanceConfig {
    enabled: boolean;
    platforms: Platform[];
    title: string;
    message: string;
    eta_iso: string | null;
    allow_admins: boolean;
    bypass_token: string | null;
    min_version_affected: string | null;
}

interface ConfigRow {
    key: string;
    value: unknown;
    updated_at: string | null;
}

const DEFAULT_CONFIG: MaintenanceConfig = {
    enabled: false,
    platforms: [],
    title: 'Estamos cebando el anzuelo',
    message:
        'Volvemos en cuanto el sonar deje de pitar. Mientras tanto, dale un repaso al equipo y prepara las cañas.',
    eta_iso: null,
    allow_admins: true,
    bypass_token: null,
    min_version_affected: null,
};

const PLATFORMS: { id: Platform; label: string; icon: React.ReactNode }[] = [
    { id: 'ios', label: 'iOS', icon: <Apple size={16} /> },
    { id: 'android', label: 'Android', icon: <Smartphone size={16} /> },
    { id: 'web', label: 'Web', icon: <Globe size={16} /> },
];

const SUGGESTED_COPIES = [
    {
        title: 'Estamos cebando el anzuelo',
        message:
            'Volvemos en cuanto el sonar deje de pitar. Mientras tanto, dale un repaso al equipo y prepara las cañas.',
    },
    {
        title: 'El sonar está en mantenimiento 🐟',
        message:
            'No encontramos peces ahora mismo. Estamos calibrando el equipo para que la próxima salida sea épica.',
    },
    {
        title: 'Echando el ancla un momento ⚓',
        message:
            'Pequeña parada técnica para dejar todo a punto. Si quieres, ve preparando el cebo.',
    },
    {
        title: 'Marejada técnica en marcha 🌊',
        message:
            'Hemos pillado mar gruesa y estamos achicando agua. Volvemos en cuanto pase la borrasca.',
    },
];

// ─── Utils ──────────────────────────────────────────────────────────────────

function normalizeMaintenance(raw: unknown): MaintenanceConfig {
    if (!raw || typeof raw !== 'object') return { ...DEFAULT_CONFIG };
    const r = raw as Record<string, unknown>;
    const platforms = Array.isArray(r.platforms)
        ? (r.platforms as unknown[]).filter(
            (p): p is Platform => p === 'ios' || p === 'android' || p === 'web'
        )
        : [];
    return {
        enabled: r.enabled === true,
        platforms,
        title: typeof r.title === 'string' ? r.title : DEFAULT_CONFIG.title,
        message: typeof r.message === 'string' ? r.message : DEFAULT_CONFIG.message,
        eta_iso: typeof r.eta_iso === 'string' ? r.eta_iso : null,
        allow_admins: r.allow_admins !== false,
        bypass_token: typeof r.bypass_token === 'string' ? r.bypass_token : null,
        min_version_affected:
            typeof r.min_version_affected === 'string' ? r.min_version_affected : null,
    };
}

/** ISO con TZ → string YYYY-MM-DDTHH:mm para <input type="datetime-local"> */
function isoToInputLocal(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
        d.getHours()
    )}:${pad(d.getMinutes())}`;
}

/** input local YYYY-MM-DDTHH:mm → ISO 8601 con TZ del navegador. */
function inputLocalToIso(local: string): string | null {
    if (!local) return null;
    const d = new Date(local);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
}

function buildBypassUrl(token: string): string {
    if (!token) return '';
    const base = window.location.origin;
    return `${base}/?maintenance_bypass=${encodeURIComponent(token)}`;
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function MaintenancePage() {
    const queryClient = useQueryClient();
    const [form, setForm] = useState<MaintenanceConfig>(DEFAULT_CONFIG);
    const [originalForm, setOriginalForm] = useState<MaintenanceConfig>(DEFAULT_CONFIG);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    // ─── Load Config ───────────────────────────
    const { data, isLoading } = useQuery({
        queryKey: ['admin', 'app-config-maintenance'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<ConfigRow[]>('admin_get_all_config');
            if (error) throw error;
            const rows = normalizeRpcResponse(data);
            const row = rows.find((r) => r.key === 'maintenance');
            return normalizeMaintenance(row?.value ?? null);
        },
    });

    useEffect(() => {
        if (data) {
            setForm(data);
            setOriginalForm(data);
        }
    }, [data]);

    const isDirty = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(originalForm),
        [form, originalForm]
    );

    // ─── Mutations ─────────────────────────────
    const saveMutation = useMutation({
        mutationFn: async (next: MaintenanceConfig) => {
            const { error } = await dbClient.rpc('admin_upsert_app_config', {
                p_key: 'maintenance',
                p_value: next,
            });
            if (error) throw error;
        },
        onSuccess: (_d, vars) => {
            setOriginalForm(vars);
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-maintenance'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config'] });
        },
    });

    const regenTokenMutation = useMutation({
        mutationFn: async () => {
            const { data, error } = await dbClient.rpc<string>(
                'admin_regenerate_maintenance_bypass_token'
            );
            if (error) throw error;
            const next = Array.isArray(data) ? (data[0] as string) : (data as string);
            return next;
        },
        onSuccess: (token) => {
            setForm((prev) => ({ ...prev, bypass_token: token }));
            setOriginalForm((prev) => ({ ...prev, bypass_token: token }));
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-maintenance'] });
        },
    });

    // ─── Handlers ──────────────────────────────
    const togglePlatform = (p: Platform) => {
        setForm((prev) => ({
            ...prev,
            platforms: prev.platforms.includes(p)
                ? prev.platforms.filter((x) => x !== p)
                : [...prev.platforms, p],
        }));
    };

    const handleSave = () => {
        if (form.enabled && !originalForm.enabled) {
            setConfirmOpen(true);
            return;
        }
        saveMutation.mutate(form);
    };

    const confirmAndSave = () => {
        setConfirmOpen(false);
        saveMutation.mutate(form);
    };

    const copyToClipboard = async (key: string, text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 1500);
        } catch {
            /* noop */
        }
    };

    const platformsLabel =
        form.platforms.length === 0
            ? 'Todas'
            : form.platforms.map((p) => p.toUpperCase()).join(' · ');

    // ─── Render ────────────────────────────────
    return (
        <div className="space-y-6">
            <PageHeader
                title="Modo Mantenimiento"
                description="Bloquea el acceso a la app móvil mostrando una pantalla informativa. Úsalo para incidencias o despliegues críticos."
            >
                <StatusBadge
                    status={originalForm.enabled ? 'active' : 'inactive'}
                    label={originalForm.enabled ? 'ACTIVO' : 'Desactivado'}
                />
            </PageHeader>

            {isLoading ? (
                <div className="py-12 flex items-center justify-center">
                    <RefreshCw size={18} className="animate-spin text-muted-foreground" />
                </div>
            ) : (
                <>
                    {/* ─── Master switch ─────────────────────────────────── */}
                    <div
                        className={cn(
                            'rounded-xl border p-5 transition-colors',
                            form.enabled
                                ? 'border-red-500/40 bg-red-500/5'
                                : 'border-border bg-card'
                        )}
                    >
                        <div className="flex items-start gap-4">
                            <div
                                className={cn(
                                    'w-12 h-12 rounded-xl flex items-center justify-center shrink-0',
                                    form.enabled
                                        ? 'bg-red-500/15 text-red-400'
                                        : 'bg-muted text-muted-foreground'
                                )}
                            >
                                <Power size={22} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <h3 className="text-base font-bold text-foreground">
                                            Activar modo mantenimiento
                                        </h3>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            Cuando esté activo, los usuarios afectados verán la
                                            pantalla en lugar de la app.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() =>
                                            setForm((p) => ({ ...p, enabled: !p.enabled }))
                                        }
                                        className={cn(
                                            'w-12 h-6 rounded-full relative shrink-0 transition-colors',
                                            form.enabled ? 'bg-red-500' : 'bg-zinc-600'
                                        )}
                                        aria-label="Toggle modo mantenimiento"
                                    >
                                        <span
                                            className={cn(
                                                'absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all shadow',
                                                form.enabled ? 'left-6' : 'left-0.5'
                                            )}
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ─── Plataformas ──────────────────────────────────── */}
                    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                        <div>
                            <h3 className="text-sm font-semibold text-foreground">
                                Plataformas afectadas
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Marca al menos una. Sin selección = afecta a TODAS (iOS, Android y Web).
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {PLATFORMS.map((p) => {
                                const active = form.platforms.includes(p.id);
                                return (
                                    <button
                                        key={p.id}
                                        onClick={() => togglePlatform(p.id)}
                                        className={cn(
                                            'inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors',
                                            active
                                                ? 'border-primary bg-primary/15 text-primary'
                                                : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-border/80'
                                        )}
                                    >
                                        {p.icon}
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            Resumen: <span className="text-foreground font-medium">{platformsLabel}</span>
                        </div>
                    </div>

                    {/* ─── Mensaje + ETA ────────────────────────────────── */}
                    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                        <h3 className="text-sm font-semibold text-foreground">
                            Mensaje al usuario
                        </h3>

                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Título</label>
                            <input
                                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                                placeholder="Estamos cebando el anzuelo"
                                value={form.title}
                                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                                maxLength={80}
                            />
                            <div className="text-right text-[10px] text-muted-foreground mt-0.5">
                                {form.title.length}/80
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-medium text-muted-foreground">
                                Mensaje (admite saltos de línea)
                            </label>
                            <textarea
                                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-y min-h-[90px]"
                                placeholder="Volvemos en cuanto el sonar deje de pitar..."
                                value={form.message}
                                onChange={(e) =>
                                    setForm((p) => ({ ...p, message: e.target.value }))
                                }
                                maxLength={300}
                            />
                            <div className="text-right text-[10px] text-muted-foreground mt-0.5">
                                {form.message.length}/300
                            </div>
                        </div>

                        {/* Plantillas rápidas */}
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                                Plantillas rápidas
                            </p>
                            <div className="grid sm:grid-cols-2 gap-2">
                                {SUGGESTED_COPIES.map((s, i) => (
                                    <button
                                        key={i}
                                        onClick={() =>
                                            setForm((p) => ({
                                                ...p,
                                                title: s.title,
                                                message: s.message,
                                            }))
                                        }
                                        className="text-left rounded-lg border border-border bg-background hover:bg-muted/40 hover:border-border/80 px-3 py-2 transition-colors"
                                    >
                                        <p className="text-xs font-semibold text-foreground line-clamp-1">
                                            {s.title}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">
                                            {s.message}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                    <Clock size={12} /> ETA estimada (opcional)
                                </label>
                                <div className="flex gap-1 mt-1">
                                    <input
                                        type="datetime-local"
                                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                                        value={isoToInputLocal(form.eta_iso)}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                eta_iso: inputLocalToIso(e.target.value),
                                            }))
                                        }
                                    />
                                    {form.eta_iso && (
                                        <button
                                            onClick={() =>
                                                setForm((p) => ({ ...p, eta_iso: null }))
                                            }
                                            className="px-2.5 rounded-lg border border-border text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                                            title="Limpiar"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1">
                                    Se mostrará como “Volvemos en ~30 min” o fecha/hora si está
                                    lejos.
                                </p>
                            </div>

                            <div>
                                <label className="text-xs font-medium text-muted-foreground">
                                    Versión máxima afectada (opcional)
                                </label>
                                <input
                                    type="text"
                                    placeholder="2.5.0"
                                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                                    value={form.min_version_affected ?? ''}
                                    onChange={(e) =>
                                        setForm((p) => ({
                                            ...p,
                                            min_version_affected: e.target.value.trim() || null,
                                        }))
                                    }
                                />
                                <p className="text-[10px] text-muted-foreground mt-1">
                                    Si se rellena, solo bloquea versiones ≤ a esta. Útil para no
                                    afectar al build en revisión de App Store/Play.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ─── Bypass token ─────────────────────────────────── */}
                    <div className="rounded-xl border border-border bg-card p-5 space-y-3">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                                    <KeyRound size={14} /> Token de bypass
                                </h3>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Comparte este token con el equipo / testers para que puedan
                                    seguir entrando aunque el modo esté activo.
                                </p>
                            </div>
                            <button
                                onClick={() => regenTokenMutation.mutate()}
                                disabled={regenTokenMutation.isPending}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                            >
                                {regenTokenMutation.isPending ? (
                                    <Loader2 size={12} className="animate-spin" />
                                ) : (
                                    <RefreshCw size={12} />
                                )}
                                Regenerar
                            </button>
                        </div>

                        <div className="flex items-center gap-2">
                            <code className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-xs font-mono text-foreground truncate">
                                {form.bypass_token ?? '— sin token —'}
                            </code>
                            <button
                                onClick={() =>
                                    form.bypass_token &&
                                    copyToClipboard('token', form.bypass_token)
                                }
                                disabled={!form.bypass_token}
                                className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                                title="Copiar token"
                            >
                                {copiedKey === 'token' ? <Check size={14} /> : <Copy size={14} />}
                            </button>
                        </div>

                        {form.bypass_token && (
                            <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-[11px] font-mono text-muted-foreground truncate">
                                    {buildBypassUrl(form.bypass_token)}
                                </code>
                                <button
                                    onClick={() =>
                                        copyToClipboard(
                                            'url',
                                            buildBypassUrl(form.bypass_token!)
                                        )
                                    }
                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
                                    title="Copiar URL de bypass"
                                >
                                    {copiedKey === 'url' ? (
                                        <Check size={14} />
                                    ) : (
                                        <Link2 size={14} />
                                    )}
                                    Copiar URL
                                </button>
                            </div>
                        )}

                        <p className="text-[10px] text-muted-foreground">
                            La URL de bypass guarda el token automáticamente en el dispositivo.
                            En consola: <code>localStorage.setItem('aipeces_maintenance_bypass', '…')</code>.
                        </p>
                    </div>

                    {/* ─── Acciones ─────────────────────────────────────── */}
                    <div className="flex items-center gap-2 sticky bottom-4 z-10 bg-background/80 backdrop-blur-sm border border-border rounded-xl p-3">
                        <button
                            onClick={handleSave}
                            disabled={!isDirty || saveMutation.isPending}
                            className={cn(
                                'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50',
                                form.enabled
                                    ? 'bg-red-500 text-white hover:bg-red-500/90'
                                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                            )}
                        >
                            {saveMutation.isPending ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Save size={14} />
                            )}
                            {saveMutation.isPending
                                ? 'Guardando…'
                                : form.enabled
                                    ? 'Guardar y ACTIVAR'
                                    : 'Guardar cambios'}
                        </button>
                        <button
                            onClick={() => {
                                setForm(originalForm);
                            }}
                            disabled={!isDirty || saveMutation.isPending}
                            className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                        >
                            Descartar
                        </button>
                        {saveMutation.isError && (
                            <span className="text-xs text-red-400">
                                Error al guardar. Revisa la consola.
                            </span>
                        )}
                        {saveMutation.isSuccess && !isDirty && (
                            <span className="text-xs text-emerald-400 flex items-center gap-1">
                                <Check size={12} /> Guardado
                            </span>
                        )}
                    </div>

                    {/* ─── Notas ────────────────────────────────────────── */}
                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-2">
                        <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5">
                            <AlertTriangle size={13} /> Notas operativas
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                            <li>
                                La pantalla aparece como mucho 60 s después de activarse en clientes
                                con la app abierta (polling). En arranques en frío es inmediato.
                            </li>
                            <li>
                                Si el endpoint público falla, los usuarios <strong>NO</strong> son
                                bloqueados (failsafe estricto).
                            </li>
                            <li>
                                Para no romper la revisión de App Store/Play, rellena{' '}
                                <em>“versión máxima afectada”</em> con la última publicada (la
                                versión en revisión es superior y queda exenta).
                            </li>
                            <li>
                                El bypass por URL: comparte la URL con el equipo, basta abrirla una
                                vez para quedar exento mientras dure el token.
                            </li>
                        </ul>
                    </div>
                </>
            )}

            {/* ─── Confirm dialog ──────────────────────────────────────── */}
            {confirmOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
                    onClick={() => setConfirmOpen(false)}
                >
                    <div
                        className="w-full max-w-md mx-4 rounded-xl border border-red-500/30 bg-card p-6 space-y-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-red-500/15 text-red-400 flex items-center justify-center shrink-0">
                                <Wrench size={18} />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-foreground">
                                    ¿Activar modo mantenimiento ahora?
                                </h3>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Los usuarios de <strong>{platformsLabel}</strong> verán la
                                    pantalla bloqueante en su próximo refresh (≤ 60 s) y al abrir
                                    la app. Asegúrate de:
                                </p>
                                <ul className="text-xs text-muted-foreground mt-2 list-disc pl-4 space-y-1">
                                    <li>Tener guardado el bypass token a mano.</li>
                                    <li>Comunicar el corte al equipo / canal de soporte.</li>
                                </ul>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={() => setConfirmOpen(false)}
                                className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmAndSave}
                                className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-500/90 transition-colors"
                            >
                                Sí, activar mantenimiento
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
